import React from 'react'
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import GetAppIcon from '@mui/icons-material/GetApp'
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer'
import { visitDashboardConstants, MEDICAL_TESTS } from '../constants/Constants'
import { makeStyles } from '../../../utils/hooks/makeStyles'
import { ScaleLoader } from 'react-spinners'

interface VisitDetail {
  'Number of Patients': string | number
  'Visits Completed': string | number
  [key: string]: string | number
}

interface ReportData {
  name?: string
  organizer?: string
  campSponsor?: string
  location?: string
  fromDate?: string
  toDate?: string
  expectedFootFall?: string | number
  numberOfParticipants?: string | number
  visitsCreated?: string | number
  visitsClosed?: string | number
  numberOfMaleParticipant?: string | number
  numberOfFemaleParticipant?: string | number
  visitsCompleteness?: string | number
  oral?: string | number
  gynEye?: string | number
  ecg?: string | number
  pft?: string | number
  psa?: string | number
  papSmear?: string | number
  iscanBreast?: string | number
  labInvestigations?: string | number
  visitsDetails?: VisitDetail[]
  [key: string]: any
}

// Styles
const useStyles = makeStyles(() => ({
  dialogRoot: {
    width: '100%',
    maxWidth: '1000px !important',
    margin: '0 auto',
    '& .MuiDialog-paper': {
      width: '100%',
      maxWidth: '1000px',
      margin: '16px',
      maxHeight: 'calc(100% - 32px)',
    },
    '@media (max-width: 600px)': {
      '& .MuiDialog-paper': {
        margin: '8px',
        maxHeight: 'calc(100% - 16px)',
      },
    },
  },
  dialog: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    lineHeight: 1.5,
    color: '#333',
    position: 'relative',
  },
  dialogTitle: {
    background: '#1976d2',
    color: 'white',
    padding: '8px 16px 8px 24px',
    margin: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: '56px',
    position: 'relative',
    borderBottom: '1px solid #e0e0e0',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginLeft: 'auto',
  },
  downloadButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#2196f3',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'none',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
    height: '36px',
    minWidth: '120px',
    '&:hover': {
      backgroundColor: '#1976d2',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
      transform: 'none',
    },
  },
  buttonIcon: {
    marginRight: '6px',
    fontSize: '1rem',
  },
  closeButton: {
    color: 'rgba(255, 255, 255, 0.9)',
    backgroundColor: 'transparent',
    margin: 0,
    padding: '8px',
    transition: 'all 0.2s ease',
    borderRadius: '50%',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      color: 'white',
      transform: 'none',
      boxShadow: 'none',
    },
    '& svg': {
      color: 'inherit',
      fontSize: '1.25rem',
    },
  },
  dialogContent: {
    padding: '24px',
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 200px)',
  },
  reportContent: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    lineHeight: 1.6,
    color: '#333',
    padding: '20px',
    background: '#fff',
    borderRadius: '4px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
  },
  reportHeader: {
    textAlign: 'center',
    margin: '0 auto 32px',
    borderRadius: '8px',
    background: 'linear-gradient(135deg, #f5f9ff 0%, #e3f2fd 100%)',
    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.1)',
    border: '1px solid #e0e8f0',
    maxWidth: '800px',
    fontFamily: 'inherit',
    padding: '24px',
  },
  campName: {
    color: '#1565c0',
    margin: '0 0 12px 0',
    fontSize: '1.75rem',
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: '0.5px',
    textShadow: '0 1px 1px rgba(0, 0, 0, 0.05)',
    '@media (max-width: 600px)': {
      fontSize: '18px',
    },
  },
  metaInfo: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    flexWrap: 'wrap',
    margin: '16px 0 0',
    padding: '12px 0 0',
    borderTop: '1px dashed #cfd8dc',
    fontSize: '0.875rem',
    color: '#455a64',
    lineHeight: 1.5,
    '& span': {
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      '&::before': {
        content: '"|"',
        color: '#90caf9',
        marginRight: '12px',
        fontWeight: 400,
      },
      '&:first-child::before': {
        display: 'none',
      },
    },
    '@media (max-width: 600px)': {
      flexDirection: 'column',
      gap: '8px',
      alignItems: 'center',
      '& span::before': {
        display: 'none',
      },
    },
  },
  metaItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: '3px',
  },
  section: {
    margin: '32px 0 40px',
    '&:first-child': {
      marginTop: 0,
    },
    '&:last-child': {
      marginBottom: 0,
    },
  },
  sectionTitle: {
    color: '#1976d2',
    fontSize: '1.25rem',
    fontWeight: 500,
    margin: '0 0 20px 0',
    paddingBottom: '8px',
    borderBottom: '1px solid #e0e0e0',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    '@media (max-width: 600px)': {
      fontSize: '16px',
    },
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    margin: '24px 0',
    '@media (max-width: 900px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
    },
  },
  statCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    textAlign: 'center',
    borderLeft: '4px solid #1976d2',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
    },
  },
  statLabel: {
    fontSize: '0.875rem',
    color: '#546e7a',
    margin: '0 0 4px 0',
    fontWeight: 500,
    letterSpacing: '0.25px',
  },
  statValue: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#1976d2',
    margin: 0,
    lineHeight: 1.2,
    letterSpacing: '0.25px',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  primaryCard: {
    position: 'relative',
    overflow: 'hidden',
    background: '#ffffff',
    border: '1px solid #e0e0e0',
    borderLeft: '4px solid #1976d2',
    '& $statValue': {
      color: '#1976d2 !important',
      opacity: 1,
    },
  },
  secondaryCard: {
    position: 'relative',
    overflow: 'hidden',
    background: '#ffffff',
    border: '1px solid #e0e0e0',
    borderLeft: '4px solid #00b09b',
    '& $statValue': {
      color: '#00b09b !important',
      opacity: 1,
    },
  },
  verticalStatCard: {
    position: 'relative',
    overflow: 'hidden',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  verticalStatItem: {
    padding: '16px 20px',
    textAlign: 'center',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '&:not(:last-child)': {
      borderBottom: '1px solid #f0f0f0',
    },
  },
  verticalStatLabel: {
    fontSize: '0.875rem',
    color: '#546e7a',
    margin: '0 0 4px 0',
    fontWeight: 500,
    letterSpacing: '0.25px',
  },
  verticalStatValue: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#1976d2',
    margin: 0,
    lineHeight: 1.2,
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  tableContainer: {
    width: '100%',
    overflowX: 'auto',
    margin: '16px 0',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
  },
  dataTable: {
    width: '100%',
    borderCollapse: 'collapse',
    margin: '20px 0',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    background: 'white',
    borderRadius: '4px',
    overflow: 'hidden',
    '& th, & td': {
      padding: '12px 15px',
      textAlign: 'left',
      borderBottom: '1px solid #e0e0e0',
      '@media (max-width: 600px)': {
        padding: '8px 10px',
        fontSize: '14px',
      },
    },
    '& th': {
      backgroundColor: '#f5f9ff',
      color: '#1976d2',
      fontWeight: 500,
      textTransform: 'uppercase',
      fontSize: '13px',
      letterSpacing: '0.5px',
    },
    '& tr:last-child td': {
      borderBottom: 'none',
    },
    '& tr:hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  thankYouNote: {
    backgroundColor: '#f8f9fa',
    padding: '20px',
    borderRadius: '6px',
    marginTop: '30px',
    borderLeft: '4px solid #4caf50',
    fontStyle: 'italic',
    color: '#2e7d32',
    '& p': {
      margin: 0,
      lineHeight: 1.6,
    },
  },
}))

// }

// PDF Styles for @react-pdf/renderer
const pdfStyles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  header: {
    textAlign: 'center',
    marginBottom: 20,
    padding: 15,
    backgroundColor: '#f5f9ff',
    borderRadius: 8,
    border: '1px solid #e0e8f0',
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1565c0',
    marginBottom: 10,
  },
  metaInfo: {
    fontSize: 9,
    color: '#455a64',
    marginTop: 8,
    paddingTop: 8,
    borderTop: '1px dashed #cfd8dc',
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 10,
  },
  metaItem: {
    marginHorizontal: 5,
    marginVertical: 2,
  },
  section: {
    marginTop: 20,
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#1976d2',
    marginBottom: 10,
    paddingBottom: 5,
    borderBottom: '1px solid #e0e0e0',
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginVertical: 10,
  },
  statCard: {
    flex: '1 1 30%',
    minWidth: 150,
    padding: 12,
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderLeft: '3px solid #1976d2',
    borderRadius: 8,
    textAlign: 'center',
  },
  statCardSecondary: {
    borderLeft: '3px solid #00b09b',
  },
  statValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1976d2',
    marginBottom: 4,
  },
  statValueSecondary: {
    color: '#00b09b',
  },
  statLabel: {
    fontSize: 9,
    color: '#546e7a',
  },
  verticalStatCard: {
    flex: '1 1 30%',
    minWidth: 150,
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
  },
  verticalStatItem: {
    padding: 10,
    textAlign: 'center',
    borderBottom: '1px solid #f0f0f0',
  },
  verticalStatItemLast: {
    borderBottom: 'none',
  },
  table: {
    marginTop: 10,
    border: '1px solid #e0e0e0',
    borderRadius: 4,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f5f9ff',
    borderBottom: '1px solid #e0e0e0',
    padding: 8,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1px solid #e0e0e0',
    padding: 8,
  },
  tableRowLast: {
    borderBottom: 'none',
  },
  tableCell: {
    flex: 1,
    fontSize: 9,
  },
  tableHeaderCell: {
    flex: 1,
    fontSize: 9,
    fontWeight: 'bold',
    color: '#1976d2',
    textTransform: 'uppercase',
  },
  thankYouNote: {
    marginTop: 20,
    padding: 12,
    backgroundColor: '#f8f9fa',
    borderLeft: '3px solid #4caf50',
    borderRadius: 4,
  },
  thankYouText: {
    fontSize: 9,
    color: '#2e7d32',
    fontStyle: 'italic',
  },
})

// Utility function to extract number from strings like "Male participants : 2" or numbers
const extractNumber = (value: string | number | undefined): string => {
  if (value === undefined) return '0'
  if (typeof value === visitDashboardConstants?.defaults?.number) {
    return value?.toString()
  }
  if (typeof value === visitDashboardConstants?.defaults?.string) {
    const strValue = String(value)
    const match = strValue.match(/\d+/)
    return match ? match[0] : '0'
  }
  return '0'
}

// Utility function to safely get and format values for display
const getValue = (value: any): string => {
  if (value === undefined || value === null) return '0'
  if (typeof value === visitDashboardConstants?.defaults?.number)
    return value?.toString()
  if (typeof value === visitDashboardConstants?.defaults?.string)
    return value || '0'
  return String(value)
}

const medicalTests = [...MEDICAL_TESTS] as const

interface ReportPreviewDialogProps {
  open: boolean
  onClose: () => void
  reportData: ReportData | null
  className?: string
  loading?: boolean
}

// PDF Document Component
const PDFDocument: React.FC<{ reportData: ReportData }> = ({ reportData }) => (
  <Document>
    <Page size='A4' style={pdfStyles.page}>
      {/* Header */}
      <View style={pdfStyles.header}>
        <Text style={pdfStyles.title}>{reportData?.name || 'Camp Report'}</Text>
        <View style={pdfStyles.metaInfo}>
          <View style={pdfStyles.metaRow}>
            {reportData?.organizer && (
              <Text style={pdfStyles.metaItem}>
                Conducted by:{' '}
                {String(reportData?.organizer)
                  ?.replace(/^Conducted by[\s:]*/i, '')
                  ?.trim() || 'N/A'}
              </Text>
            )}
            {reportData?.campSponsor && (
              <Text style={pdfStyles.metaItem}>
                Sponsored by:{' '}
                {String(reportData?.campSponsor)
                  ?.replace(/^Sponsored by[\s:]*/i, '')
                  ?.trim() || 'N/A'}
              </Text>
            )}
          </View>
          <View style={pdfStyles.metaRow}>
            {reportData?.location && (
              <Text style={pdfStyles.metaItem}>
                Location:{' '}
                {String(reportData?.location)
                  ?.replace(/^At[\s:]*/i, '')
                  ?.trim()}
              </Text>
            )}
            {(reportData?.fromDate || reportData?.toDate) && (
              <Text style={pdfStyles.metaItem}>
                Date:{' '}
                {reportData?.fromDate && (
                  <>
                    {String(reportData?.fromDate)
                      ?.replace(/^From[\s:]*/i, '')
                      ?.trim()}
                  </>
                )}
                {reportData?.toDate && (
                  <>
                    {reportData?.fromDate ? ' to ' : ''}
                    {String(reportData?.toDate)
                      ?.replace(/^To[\s:]*/i, '')
                      ?.trim()}
                  </>
                )}
              </Text>
            )}
          </View>
        </View>
      </View>

      {/* Camp Statistics */}
      <View style={pdfStyles.section}>
        <Text style={pdfStyles.sectionTitle}>Camp Statistics</Text>
        <View style={pdfStyles.statsGrid}>
          {/* Expected Footfall */}
          <View style={pdfStyles.statCard}>
            <Text style={pdfStyles.statValue}>
              {reportData?.expectedFootFall !== undefined &&
              reportData?.expectedFootFall !== null &&
              String(reportData.expectedFootFall).trim() !== ''
                ? String(reportData.expectedFootFall).replace(/[^\d.]/g, '')
                : '0'}
            </Text>
            <Text style={pdfStyles.statLabel}>Expected Footfall</Text>
          </View>

          {/* Participants */}
          <View style={pdfStyles.verticalStatCard}>
            <View style={pdfStyles.verticalStatItem}>
              <Text style={pdfStyles.statLabel}>Total Participants</Text>
              <Text style={pdfStyles.statValue}>
                {String(reportData?.numberOfParticipants ?? '')?.replace(
                  /^[^:]+:\s*/,
                  ''
                ) || '0'}
              </Text>
            </View>
            <View
              style={[
                pdfStyles.verticalStatItem,
                pdfStyles.verticalStatItemLast,
              ]}
            >
              <Text style={pdfStyles.statLabel}>Gender Distribution</Text>
              <Text style={pdfStyles.statValue}>
                {`${
                  extractNumber(reportData?.numberOfMaleParticipant) || '0'
                } M | ${
                  extractNumber(reportData?.numberOfFemaleParticipant) || '0'
                } F`}
              </Text>
            </View>
          </View>

          {/* Visits */}
          <View style={pdfStyles.verticalStatCard}>
            <View style={pdfStyles.verticalStatItem}>
              <Text style={pdfStyles.statLabel}>Total Visits</Text>
              <Text style={pdfStyles.statValue}>
                {String(reportData?.visitsCreated ?? '')?.replace(
                  /^[^:]+:\s*/,
                  ''
                ) || '0'}
              </Text>
            </View>
            <View
              style={[
                pdfStyles.verticalStatItem,
                pdfStyles.verticalStatItemLast,
              ]}
            >
              <Text style={pdfStyles.statLabel}>Visits Closed</Text>
              <Text style={pdfStyles.statValue}>
                {String(reportData?.visitsClosed ?? '')?.replace(
                  /^[^:]+:\s*/,
                  ''
                ) || '0'}
              </Text>
            </View>
          </View>

          {/* Completion Rate */}
          <View style={[pdfStyles.statCard, pdfStyles.statCardSecondary]}>
            <Text style={[pdfStyles.statValue, pdfStyles.statValueSecondary]}>
              {reportData?.visitsCompleteness !== undefined &&
              reportData?.visitsCompleteness !== null &&
              String(reportData.visitsCompleteness).trim() !== ''
                ? (
                    parseFloat(
                      String(reportData.visitsCompleteness).replace(
                        /[^0-9.]/g,
                        ''
                      )
                    ) || 0
                  ).toFixed(2) + '%'
                : '0.00%'}
            </Text>
            <Text style={pdfStyles.statLabel}>Completion Rate</Text>
          </View>
        </View>
      </View>

      {/* Visits Distribution */}
      {reportData?.visitsDetails && reportData?.visitsDetails?.length > 0 && (
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.sectionTitle}>Visits Distribution</Text>
          <View style={pdfStyles.table}>
            <View style={pdfStyles.tableHeader}>
              <Text style={pdfStyles.tableHeaderCell}>Number of Patients</Text>
              <Text style={pdfStyles.tableHeaderCell}>Visits Completed</Text>
            </View>
            {reportData?.visitsDetails?.map((item, index) => (
              <View
                key={index}
                style={
                  index === (reportData?.visitsDetails?.length ?? 0) - 1
                    ? [pdfStyles.tableRow, pdfStyles.tableRowLast]
                    : pdfStyles.tableRow
                }
              >
                <Text style={pdfStyles.tableCell}>
                  {item['Number of Patients']}
                </Text>
                <Text style={pdfStyles.tableCell}>
                  {item['Visits Completed']}
                </Text>
              </View>
            ))}
          </View>
        </View>
      )}

      {/* Medical Services */}
      <View style={pdfStyles.section}>
        <Text style={pdfStyles.sectionTitle}>Medical Services</Text>
        <View style={pdfStyles.table}>
          <View style={pdfStyles.tableHeader}>
            <Text style={pdfStyles.tableHeaderCell}>Service Type</Text>
            <Text style={pdfStyles.tableHeaderCell}>Count</Text>
          </View>
          {medicalTests?.map((test, index) => {
            const value = reportData[test?.key]
            return (
              <View
                key={test?.key}
                style={
                  index === medicalTests.length - 1
                    ? [pdfStyles.tableRow, pdfStyles.tableRowLast]
                    : pdfStyles.tableRow
                }
              >
                <Text style={pdfStyles.tableCell}>{test?.label}</Text>
                <Text style={pdfStyles.tableCell}>{getValue(value || 0)}</Text>
              </View>
            )
          })}
        </View>
      </View>

      {/* Thank You Note */}
      {reportData?.thankYouNote && (
        <View style={pdfStyles.thankYouNote}>
          <Text style={pdfStyles.thankYouText}>{reportData?.thankYouNote}</Text>
        </View>
      )}
    </Page>
  </Document>
)

// Report Content Component for Preview
const ReportContent = React.forwardRef<
  HTMLDivElement,
  { reportData: ReportData; classes: Record<string, string> }
>(({ reportData, classes }, ref) => (
  <div ref={ref} className={classes.reportContent}>
    <div className={classes.reportHeader}>
      <h1>{reportData?.name || 'Camp Report'}</h1>
      <div className={classes.metaInfo}>
        {reportData?.organizer && (
          <span className={classes.metaItem}>
            <strong>Conducted by:</strong>{' '}
            {String(reportData?.organizer)
              ?.replace(/^Conducted by[\s:]*/i, '')
              ?.trim() || 'N/A'}
          </span>
        )}
        {reportData?.campSponsor && (
          <span className={classes.metaItem}>
            <strong>Sponsored by:</strong>{' '}
            {String(reportData?.campSponsor)
              ?.replace(/^Sponsored by[\s:]*/i, '')
              ?.trim() || 'N/A'}
          </span>
        )}
        {reportData?.location && (
          <span className={classes.metaItem}>
            <strong>Location:</strong>{' '}
            {String(reportData?.location)
              ?.replace(/^At[\s:]*/i, '')
              ?.trim()}
          </span>
        )}
        {(reportData?.fromDate || reportData?.toDate) && (
          <span className={classes.metaItem}>
            <strong>Date:</strong>{' '}
            {reportData?.fromDate && (
              <>
                {String(reportData?.fromDate)
                  ?.replace(/^From[\s:]*/i, '')
                  ?.trim()}
              </>
            )}
            {reportData?.toDate && (
              <>
                {reportData?.fromDate ? ' to ' : ''}
                {String(reportData?.toDate)
                  ?.replace(/^To[\s:]*/i, '')
                  ?.trim()}
              </>
            )}
          </span>
        )}
      </div>
    </div>

    <div className={classes.section}>
      <h2 className={classes.sectionTitle}>Camp Statistics</h2>
      <div className={classes.statsGrid}>
        {/* Expected Footfall */}
        <div className={`${classes.statCard} ${classes.primaryCard}`}>
          <div className={classes.statValue} data-testid='expected-footfall'>
            {reportData?.expectedFootFall !== undefined &&
            reportData?.expectedFootFall !== null &&
            String(reportData.expectedFootFall).trim() !== ''
              ? String(reportData.expectedFootFall).replace(/[^\d.]/g, '')
              : '0'}
          </div>
          <div className={classes.statLabel}>Expected Footfall</div>
        </div>

        {/* Participants Tile */}
        <div className={classes.verticalStatCard}>
          <div className={classes.verticalStatItem}>
            <div className={classes.verticalStatLabel}>Total Participants</div>
            <div
              className={classes.verticalStatValue}
              data-testid='total-participants'
            >
              {String(reportData?.numberOfParticipants ?? '')?.replace(
                /^[^:]+:\s*/,
                ''
              ) || '0'}
            </div>
          </div>
          <div className={classes.verticalStatItem}>
            <div className={classes.verticalStatLabel}>Gender Distribution</div>
            <div className={classes.verticalStatValue}>
              {`${
                extractNumber(reportData?.numberOfMaleParticipant) || '0'
              } M | ${
                extractNumber(reportData?.numberOfFemaleParticipant) || '0'
              } F`}
            </div>
          </div>
        </div>

        {/* Visits Tile */}
        <div className={classes.verticalStatCard}>
          <div className={classes.verticalStatItem}>
            <div className={classes.verticalStatLabel}>Total Visits</div>
            <div
              className={classes.verticalStatValue}
              data-testid='total-visits'
            >
              {String(reportData?.visitsCreated ?? '')?.replace(
                /^[^:]+:\s*/,
                ''
              ) || '0'}
            </div>
          </div>
          <div className={classes.verticalStatItem}>
            <div className={classes.verticalStatLabel}>Visits Closed</div>
            <div
              className={classes.verticalStatValue}
              data-testid='visits-closed'
            >
              {String(reportData?.visitsClosed ?? '')?.replace(
                /^[^:]+:\s*/,
                ''
              ) || '0'}
            </div>
          </div>
        </div>

        {/* Completion Rate */}
        <div className={`${classes.statCard} ${classes.secondaryCard}`}>
          <div className={classes.statValue} data-testid='completion-rate'>
            {reportData?.visitsCompleteness !== undefined &&
            reportData?.visitsCompleteness !== null &&
            String(reportData.visitsCompleteness).trim() !== ''
              ? (
                  parseFloat(
                    String(reportData.visitsCompleteness).replace(
                      /[^0-9.]/g,
                      ''
                    )
                  ) || 0
                ).toFixed(2) + '%'
              : '0.00%'}
          </div>
          <div className={classes.statLabel}>Completion Rate</div>
        </div>
      </div>
    </div>

    {/* Visits Distribution */}
    {reportData?.visitsDetails && reportData?.visitsDetails?.length > 0 && (
      <div className={classes.section}>
        <h2 className={classes.sectionTitle}>Visits Distribution</h2>
        <div className={classes.tableContainer}>
          <table className={classes.dataTable}>
            <thead>
              <tr>
                <th>Number of Patients</th>
                <th>Visits Completed</th>
              </tr>
            </thead>
            <tbody>
              {reportData?.visitsDetails?.map((item, index) => (
                <tr key={index}>
                  <td>{item['Number of Patients']}</td>
                  <td>{item['Visits Completed']}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}

    {/* Medical Tests */}
    {/* Medical Services */}
    <div className={classes.section}>
      <h2 className={classes.sectionTitle}>Medical Services</h2>
      <div className={classes.tableContainer}>
        <table className={classes.dataTable}>
          <thead>
            <tr>
              <th>Service Type</th>
              <th>Count</th>
            </tr>
          </thead>
          <tbody>
            {medicalTests?.map((test) => {
              const value = reportData[test?.key]
              return (
                <tr key={test?.key}>
                  <td>{test?.label}</td>
                  <td>{getValue(value || 0)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>

    {/* Thank You Note */}
    {reportData?.thankYouNote && (
      <div className={classes.thankYouNote}>
        <p>{reportData?.thankYouNote}</p>
      </div>
    )}
  </div>
))

ReportContent.displayName = 'ReportContent'

const ReportPreviewDialog: React.FC<ReportPreviewDialogProps> = ({
  open,
  onClose,
  reportData,
  loading,
}) => {
  const classes = useStyles()

  const handleDownload = async () => {
    if (!reportData) return

    try {
      const blob = await pdf(<PDFDocument reportData={reportData} />).toBlob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = reportData?.name
        ? `${reportData.name} Report.pdf`
        : 'Camp Report.pdf'
      link.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error generating PDF:', error)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      className={classes.dialogRoot}
      classes={{
        paper: classes.dialog,
      }}
      onClick={(e) => e?.stopPropagation()}
    >
      <DialogTitle className={classes.dialogTitle}>
        <div style={{ fontSize: '1.25rem', fontWeight: 500 }}>Camp Report</div>
        <div className={classes.headerActions}>
          {!loading && reportData && (
            <button
              onClick={handleDownload}
              className={classes.downloadButton}
              aria-label='Download PDF'
            >
              <GetAppIcon className={classes.buttonIcon} />
              <span>Download PDF</span>
            </button>
          )}
          <IconButton
            onClick={onClose}
            size='medium'
            className={classes.closeButton}
            aria-label='close'
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
            }}
          >
            <ScaleLoader />
          </div>
        ) : reportData ? (
          <ReportContent reportData={reportData} classes={classes} />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            No report data available
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ReportPreviewDialog
